import React, {FC, memo, useEffect, useState} from "react";
import {isBrowser} from "@montana/helpers/browser.helper";
import {IM03VariantCard, IM03VariantCarousel, IM03VariantCarouselTab} from "@montana/models/generated/cms.interface";
import {ProductCard} from "@montana/shared/product-card";
import {Link} from "@montana/shared/link";
import {RichText} from "@montana/shared/rich-text";
import {Button, ButtonStyles} from "@montana/shared/button";
import {Responsive} from "@montana/shared/responsive";
import {Dropdown} from "@montana/shared/dropdown";
import {IFieldOptionValue} from "@montana/shared/form/field/field";
import {useTranslations} from "@montana/containers/translations";
import Carousel from "@montana/shared/carousel";
import {getColorSwatchesById} from "@montana/helpers/color.helper";
import {useDynamicSettings} from "@montana/containers/dynamic-settings";

export const M03VariantCarouselBlock: FC<IM03VariantCarousel> = memo(({ header, carouselTabs, viewAllLink }: IM03VariantCarousel) => {
    if (!isBrowser) return <></>;

    const [activeTab, setActiveTab] = useState<IM03VariantCarouselTab | undefined>((carouselTabs?.length === 1) ? carouselTabs[0] : undefined);
    const [shownVariants, setShownVariants] = useState<IM03VariantCard[]>([]);
    const [tabOptions, setTabOptions] = useState<IFieldOptionValue[]>([]);

    const {colors = []} = useDynamicSettings();
    const translate = useTranslations();

    useEffect(() => {
        if (!activeTab) {
            showAll();
        } else {
            setShownVariants(activeTab?.variants || []);
        }
    }, [activeTab])

    useEffect(() => {
        let carouselTabOptions: IFieldOptionValue[] = [];
        if (!!carouselTabs && carouselTabs.length > 1) {
            carouselTabOptions = [{ value: '-1', text: translate(t => t.general.all, '[All]') }];
        }
        carouselTabOptions = [...carouselTabOptions, ...(carouselTabs?.map((tab, i) => ({ value: `${i}`, text: tab.title })) || [])];
        setTabOptions(carouselTabOptions);
    }, [carouselTabs])

    const showAll = () => {
        setShownVariants(carouselTabs?.map(tab => tab?.variants || [])
            .reduce((acc, variants) => [...acc, ...variants], []) || []);
    }

    return (
        <section className="variant-carousel">
            {header && <RichText className="variant-carousel__header" text={header} disableAppear />}
            {shownVariants && (
                <Carousel
                    header={(
                        <div className="variant-carousel__navigation">
                            <Responsive maxWidth={600}>
                                {tabOptions?.length > 1 ? (
                                    <Dropdown
                                        className="variant-carousel__tab-dropdown"
                                        options={tabOptions}
                                        value={activeTab ? `${carouselTabs?.indexOf(activeTab)}` : '-1'}
                                        onChange={(_: string, __: string, index: number) => setActiveTab(() => carouselTabs && index > 0 ? carouselTabs[index - 1] : undefined)}
                                    />
                                ) : <div></div>}
                            </Responsive>
                            <Responsive minWidth={601}>
                                <div className="variant-carousel__navigation-tabs">
                                    {!!carouselTabs && carouselTabs.length > 1 && <Button inverseStrokeAnimation strokeIsActive={!activeTab} style={ButtonStyles.Text} onClick={() => setActiveTab(undefined)} text={translate(t => t.general.all, '[All]')} />}
                                    {carouselTabs?.filter(tab => !!tab.variants?.length)
                                        .map((tab, index) => <Button key={index} inverseStrokeAnimation strokeIsActive={activeTab?.title === tab.title} style={ButtonStyles.Text} onClick={() => setActiveTab(tab)} text={tab.title || ''} />)
                                    }
                                </div>
                            </Responsive>
                            {!!viewAllLink && (
                                <Link
                                    to={viewAllLink?.url}
                                    title={viewAllLink?.title}
                                    withStroke
                                >
                                    {viewAllLink?.name}
                                </Link>
                            )}
                        </div>
                    )}
                    lowHeaderSpacing
                    insideBrokenContainer
                >
                    {shownVariants.map((variant: IM03VariantCard, index: number) => (
                        <ProductCard
                            key={`variant-carousel__card__${variant.itemId}`}
                            className="variant-carousel__card"
                            title={variant.name}
                            dimensions={variant.dimensions}
                            customImage={variant.productImageViewModel?.useAssetImage ? variant.productImageViewModel?.assetImageViewModel : undefined}
                            // imageUrl={variant.productImageViewModel?.imageRenderingMethod === LondonDynamicsImageRendering ? variant.productImageViewModel?.londonDynamicsStaticImageUrl : variant.productImageViewModel?.cylindoImageUrl}
                            imageUrl={variant.productImageViewModel?.londonDynamicsStaticImageUrl}
                            link={{ url: variant.productUrl, title: variant.name }}
                            price={variant.price}
                            gtmProduct={{
                                id: variant.itemId || '',
                                name: variant.name || '',
                                list: '',
                                position: index + 1,
                                price: variant.price?.priceWithDiscount?.amount || 0,
                            }}
                            sizes="320px"
                            colourSwatches={getColorSwatchesById(variant.variantColours, colors)}
                            numberOfVariantColours={variant.numberOfVariantColours}
                        />
                    ))}
                </Carousel>
            )}
        </section >
    );
});
